// src/hooks/useClientStatusTags.ts
// Хук для интеграции тэгов статуса клиентов с централизованной системой тэгов

import { useMemo } from 'react';
import { useTagsUnified } from '@/domains/tags/hooks/useTagsUnified';

/**
 * Хук для получения тэгов статуса клиентов
 * Интегрирует централизованную систему тэгов для управления статусами лояльности клиентов
 */
export function useClientStatusTags() {
  const { tags, loading, error, createTag } = useTagsUnified({ type: 'client' });

  // Фильтруем тэги для статуса клиентов (без родительского тэга = корневые тэги статуса)
  const statusTags = useMemo(() => 
    tags.filter(tag => !tag.parentId), 
    [tags]
  );

  // Преобразуем в опции для селектора
  const statusOptions = useMemo(() => 
    statusTags.map(tag => ({
      value: tag.id,
      label: tag.name,
      color: tag.color
    })), 
    [statusTags]
  );

  // Получение названия статуса по ID
  const getStatusLabel = (statusId: string | null): string => {
    if (!statusId) return 'Не указан';
    const tag = statusTags.find(tag => tag.id === statusId);
    return tag?.name || 'Неизвестный статус';
  };

  // Получение цвета статуса по ID
  const getStatusColor = (statusId: string | null): string | null => {
    if (!statusId) return null;
    const tag = statusTags.find(tag => tag.id === statusId);
    return tag?.color || null;
  };

  // Создание нового тэга статуса клиента
  const createStatusTag = async (name: string, color?: string) => {
    return await createTag({
      name,
      type: 'client',
      parentId: null,
      color: color || null,
      description: `Статус лояльности клиента: ${name}`
    });
  };

  return {
    statusTags,
    statusOptions,
    loading,
    error,
    getStatusLabel,
    getStatusColor,
    createStatusTag
  };
}
